import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { QuestionData, QuestionModuleData, StreamData, QuestionModule } from '@/domains/seek/types';
import { QuestionModuleDataStatus, QuestionStatus, QuestionModuleApiType, StreamStatus, QuestionServerSideStatus } from '@/domains/seek/types';
import { PageRepository } from '@/domains/seek/services/page-repository';
import type { PageDataService } from '../page-data-service';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { PageModel } from '@/domains/seek/models/page-model';
import _ from 'lodash';

@injectable()
export class PageDataServiceImpl implements PageDataService {
    constructor(
        @inject(PageRepository)
        private _pageRepository: PageRepository,
        @inject(PageModel)
        private _pageModel: PageModel
    ) {
    }

    private _sseInstancesMap = new Map<string, PsHttpSSE<StreamData>[]>();

    buildHttpModulePromise(questionData: QuestionData, module: QuestionModuleData, isRegenerate = false): () => Promise<unknown> {
        return () => {
            const currentRequestSignal = this._pageModel.modelData._request_signal;
            const moduleRequestSignal = module._request_signal;
            if (moduleRequestSignal !== currentRequestSignal) return Promise.resolve();
            this._pageModel.updateModuleData(questionData.question_id, module.module_name, (v: QuestionModuleData) => {
                v._module_status = QuestionModuleDataStatus.fetching;
                return v
            })
            this._pageModel.updateQuestionData(questionData.question_id, (q) => {
                q._question_status = QuestionStatus.streaming;
                return q
            })
            return this._pageRepository.request({
                url: module.api_path,
                method: 'POST',
                data: {
                    seek_id: questionData.seek_id,
                    question_id: questionData.question_id,
                    regenerate: isRegenerate,
                },
            }).then((data) => {
                this._pageModel.updateModuleData(questionData.question_id, module.module_name, (v: QuestionModuleData) => {
                    v.content = data.data;
                    v._module_status = QuestionModuleDataStatus.success;
                    v.status = QuestionServerSideStatus.FINISH;
                    return v
                })
                return this._pageModel.getModuleData(questionData.question_id, module.module_name);
            }).catch(() => {
                this._pageModel.updateModuleData(questionData.question_id, module.module_name, (v: QuestionModuleData) => {
                    v._module_status = QuestionModuleDataStatus.failed;
                    return v
                })
            })
        }
    }

    private _onSseOpen(questionData: QuestionData, module: QuestionModuleData) {
        this._pageModel.updateModuleData(questionData.question_id, module.module_name, (m) => {
            m._module_status = QuestionModuleDataStatus.streaming;
            return m;
        })
        this._pageModel.updateQuestionData(questionData.question_id, (q) => {
            q._question_status = QuestionStatus.streaming;
            return q
        })
    }

    private _onSseMessage(resolve: (value: unknown) => void, reject: (reason?: any) => void, questionData: QuestionData, module: QuestionModuleData, data: StreamData) {
        const { status, message } = data
        if (status === StreamStatus.PROCESSING) {
            this._pageModel.updateModuleData(questionData.question_id, module.module_name, (m) => {
                m._module_status = QuestionModuleDataStatus.streaming;
                if (message) m.content = message;
                return m;
            })
        } else if (status === StreamStatus.FAILED) {
            this._pageModel.updateModuleData(questionData.question_id, module.module_name, (m) => {
                m._module_status = QuestionModuleDataStatus.failed;
                return m;
            })
            reject(message);
        }
    }

    private _onSseFinish(resolve: (value: unknown) => void, reject: (reason?: any) => void, questionData: QuestionData, module: QuestionModuleData) {
        this._pageModel.updateModuleData(questionData.question_id, module.module_name, (m) => {
            m._module_status = QuestionModuleDataStatus.success;
            m.status = QuestionServerSideStatus.FINISH;
            return m;
        })
        resolve(this._pageModel.getModuleData(questionData.question_id, module.module_name));
    }

    private _onSseClose(sse: PsHttpSSE<StreamData> | null, question: QuestionData) {
        if (sse) {
            const sseInstances = this._sseInstancesMap.get(question.question_id) || [];
            this._sseInstancesMap.set(question.question_id, sseInstances.filter(s => s !== sse));
            sse = null
        }
    }

    private _onSseError(reject: (reason?: any) => void, questionData: QuestionData, module: QuestionModuleData, error: unknown) {
        this._pageModel.updateModuleData(questionData.question_id, module.module_name, (m) => {
            m._module_status = QuestionModuleDataStatus.failed;
            return m;
        })
        reject(error);
    }

    terminateSseInstances() {
        this._pageModel.update((p) => {
            p._request_signal = '';
            p._page_status = QuestionStatus.done;
            return p;
        })
        for (const sseInstances of this._sseInstancesMap.values()) {
            for (const sse of sseInstances) {
                sse.close()
            }
        }
        const questionIds = Array.from(this._sseInstancesMap.keys());
        for (const questionId of questionIds) {
            this._pageRepository.stopPageStreamEvent(questionId);
        }
        this._sseInstancesMap.clear();
        const questionList:QuestionData[] = this._pageModel.modelData._question_list
        for (const question of questionList) {
            for (const module of question.module_data) {
                if (![QuestionModuleDataStatus.success, QuestionModuleDataStatus.failed].includes(module._module_status || QuestionModuleDataStatus.starting)) {
                    this._pageModel.updateModuleData(question.question_id, module.module_name, (m) => {
                        m._module_status = QuestionModuleDataStatus.success;
                        m.status = QuestionServerSideStatus.FINISH;
                        return m;
                    })
                }
            }
            this._pageModel.updateQuestionData(question.question_id, (q) => {
                q._question_status = QuestionStatus.done;
                return q;
            })
        }
    }

    buildSseModulePromise(questionData: QuestionData, module: QuestionModuleData, isRegenerate = false): () => Promise<unknown> {
        return () => {
            const currentRequestSignal = this._pageModel.modelData._request_signal;
            const moduleRequestSignal = module._request_signal;
            if (moduleRequestSignal !== currentRequestSignal) return Promise.resolve();
            this._pageModel.updateModuleData(questionData.question_id, module.module_name, (v: QuestionModuleData) => {
                v._module_status = QuestionModuleDataStatus.streaming;
                return v
            })
            this._pageModel.updateQuestionData(questionData.question_id, (q) => {
                q._question_status = QuestionStatus.streaming;
                return q
            })
            return new Promise((resolve, reject) => {
                let sse: PsHttpSSE<StreamData> | null = null;
                sse = this._pageRepository.getPageStream<StreamData>({
                    seek_id: questionData.seek_id,
                    question_id: questionData.question_id,
                    module: module.module_name,
                    regenerate: isRegenerate,
                    path: module.api_path,
                })
                const sseInstances = this._sseInstancesMap.get(questionData.question_id) || [];
                sseInstances.push(sse);
                this._sseInstancesMap.set(questionData.question_id, sseInstances);
                sse.onOpen(() => { this._onSseOpen(questionData, module) })
                sse.onMessage(({ data }) => { this._onSseMessage(resolve, reject, questionData, module, data) })
                sse.onFinish(() => { this._onSseFinish(resolve, reject, questionData, module) })
                sse.onError((error) => { this._onSseError(reject, questionData, module, error) })
                sse.onClose(() => { this._onSseClose(sse, questionData) })
                sse.start();
            }).catch(() => {
                this._pageModel.updateModuleData(questionData.question_id, module.module_name, (v: QuestionModuleData) => {
                    v._module_status = QuestionModuleDataStatus.failed;
                    return v
                })
            })
        }
    }

    orderModulesPromiseByPriority(questionData: QuestionData, requestSignal: string, isRegenerate = false): [QuestionModuleData[], (() => Promise<unknown>)[][]] {
        const modulesPromise: (() => Promise<unknown>)[][] = [];
        const modules = questionData.module_data;
        for (const module of modules) {
            if (module.status === QuestionServerSideStatus.FINISH) {
                module._module_status = QuestionModuleDataStatus.success;
                continue;
            }
            module._module_status = QuestionModuleDataStatus.starting;
            module.content = undefined;
            module._request_signal = requestSignal;
            const priority = module.priority || 0;
            if (!modulesPromise[priority]) modulesPromise[priority] = [];
            if (module.api_type === QuestionModuleApiType.http) {
                modulesPromise[priority].push(this.buildHttpModulePromise(questionData, module, isRegenerate))
            } else if (module.api_type === QuestionModuleApiType.sse) {
                modulesPromise[priority].push(this.buildSseModulePromise(questionData, module, isRegenerate))
            }
        }
        return [modules, modulesPromise];
    }

    async requestModulesPromise(questionData: QuestionData, modulesPromise: (() => Promise<unknown>)[][]) {
        this._pageModel.updateQuestionData(questionData.question_id, (q) => {
            q._question_status = QuestionStatus.starting
            return q
        })
        for (const modulePromise of modulesPromise) {
            if (!modulePromise || modulePromise.length === 0) continue;
            await Promise.all(modulePromise.map(p => p()))
        }
        this._pageModel.updateQuestionData(questionData.question_id, (q) => {
            q._question_status = QuestionStatus.done
            return q
        })
    }

    async processAllQuestionModuleData(questionDataList: QuestionData[], requestSignal: string): Promise<QuestionData[]> {
        this._pageModel.update((p) => {
            p._page_status = QuestionStatus.streaming
            p._question_list = _.uniqBy([...p._question_list, ...questionDataList], 'question_id')
            return p
        })
        for (const questionData of questionDataList) {
            const [modules, modulesPromise] = this.orderModulesPromiseByPriority(questionData, requestSignal)
            this._pageModel.updateQuestionData(questionData.question_id, (q) => {
                q.module_data = modules;
                return q
            })
            await this.requestModulesPromise(questionData, modulesPromise)
        }
        this._pageModel.update((p) => {
            p._page_status = QuestionStatus.done
            return p
        })
        return this._pageModel.modelData._question_list;
    }

    async reProcessPartialQuestionModuleData(questionData: QuestionData, module_names: QuestionModule[], requestSignal: string) {
        this._pageModel.update((p) => {
            p._page_status = QuestionStatus.streaming
            return p
        })
        for (const module of questionData.module_data) {
            if (!module_names.includes(module.module_name)) continue;
            module.status = QuestionServerSideStatus.INIT;
        }
        const [modules, modulesPromise] = this.orderModulesPromiseByPriority(questionData, requestSignal, true)
        this._pageModel.updateQuestionData(questionData.question_id, (q) => {
            q.module_data = modules;
            return q
        })
        await this.requestModulesPromise(questionData, modulesPromise)
        this._pageModel.update((p) => {
            p._page_status = QuestionStatus.done
            return p
        })
        return this._pageModel.modelData._question_list;
    }
}
